import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} days - Days remaining.
 * @property {HTMLElement} hours - Hours remaining.
 * @property {HTMLElement} minutes - Minutes remaining.
 * @property {HTMLElement} seconds - Seconds remaining.
 */

/**
 * Counts down to a fixed deadline. Liquid renders the remaining time at
 * request time, so the markup is already correct without JavaScript; this
 * keeps it ticking and hides the timer once the deadline passes (a cached
 * page can outlive it). With the `daily` attribute the deadline is midnight
 * and rolls forward a day whenever it passes, so the clock starts over every
 * day and a cached page never shows yesterday's.
 *
 * @extends {Component<Refs>}
 */
class CountdownTimer extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #interval;

  /** Deadline as a Unix timestamp in milliseconds */
  #end = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#end = Number(this.getAttribute('end-timestamp')) * 1000;
    if (!this.#end) return;

    this.#update();
    this.#interval = window.setInterval(() => this.#update(), 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#interval);
  }

  #update() {
    if (this.hasAttribute('daily')) {
      // Midnight has passed (or the page came from a cache): the next one
      while (this.#end <= Date.now()) this.#end += 86400 * 1000;
    }
    const remaining = Math.floor((this.#end - Date.now()) / 1000);

    if (remaining <= 0) {
      window.clearInterval(this.#interval);
      this.hidden = true;
      return;
    }

    const { days, hours, minutes, seconds } = this.refs;
    days.textContent = String(Math.floor(remaining / 86400)).padStart(2, '0');
    hours.textContent = String(Math.floor((remaining % 86400) / 3600)).padStart(2, '0');
    minutes.textContent = String(Math.floor((remaining % 3600) / 60)).padStart(2, '0');
    seconds.textContent = String(remaining % 60).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
